import { z } from "zod";
import type { FinancialYear } from "../types.js";

// ---------------------------------------------------------------------------
// super-boost — the low/middle-income "super money you're missing + catch-up"
// tool family. Four pure calc surfaces over one FY-versioned ruleset:
//
//   coContribution      — government co-contribution (up to $500, 50% match)
//   spouseOffset        — spouse-contribution tax offset (up to $540)
//   carryForwardHeadroom— unused concessional-cap carry-forward (5-yr window)
//   sacrificeTaxSaving  — salary-sacrifice / personal-deductible concession
//
// This is NOT a per-product ruleset like au06/au08 — super is not one of the
// PRODUCTS codes — so its rulesets carry an `engine: "super-boost"` tag plus the
// usual provenance fields (mirroring the income-tax engine). Calculation is
// pure: inputs -> rules -> outputs, the assessment FY is always passed in, no
// Date.now(), no I/O.
//
// HARD RULE (binding, panel C2): superannuation is an s764A financial product
// with NO carve-out. NO fund / super-product / SMSF / "boost your super"
// identifier may ever reach a personalised-result output from this surface —
// $0 affiliate by design. This is encoded as a guard (`assertNoFundHandoff`)
// in calculate.ts and asserted in the tests, not left as doc prose.
// ---------------------------------------------------------------------------

/** A complete FY super-boost ruleset, keyed by financial year. */
export interface SuperBoostRuleSet {
  /** Engine tag — distinguishes shared engines from per-product rulesets. */
  engine: "super-boost";
  /** Financial-year key, e.g. "2025-26". */
  fy: FinancialYear;
  /** Inclusive FY start (1 July). */
  effectiveFrom: string;
  /** Inclusive FY end (30 June). */
  effectiveTo: string;
  /** Source-of-truth URLs the figures were transcribed from. */
  sources: Record<string, string>;
  /** Date the source pages were last verified, ISO. */
  sourcesVerifiedAt: string;

  // --- Government co-contribution ---
  coContribution: CoContributionRules;
  // --- Spouse contribution tax offset ---
  spouseOffset: SpouseOffsetRules;
  // --- Concessional cap (carry-forward + sacrifice concession) ---
  /** General concessional contributions cap for THIS FY (e.g. $30,000). */
  concessionalCap: number;
  /** Flat in-fund tax on concessional contributions (e.g. 0.15 = 15%). */
  concessionalTaxRate: number;
  /** Carry-forward rule constants (5-year rolling window + $500k TSB gate). */
  carryForward: CarryForwardRules;
  /** Low Income Super Tax Offset — refunds the 15% in-fund tax for low earners. */
  listo: ListoRules;
}

/**
 * Government super co-contribution. The ATO pays 50c per $1 of eligible
 * (non-concessional, after-tax) personal contribution, to a maximum of `maxAmount`
 * at/below `lowerThreshold`, tapering by `taperRate` per $1 of total income above
 * the lower threshold to nil at `higherThreshold`.
 */
export interface CoContributionRules {
  /** Maximum co-contribution (e.g. $500). */
  maxAmount: number;
  /** Matching rate on eligible personal contributions (e.g. 0.5 = 50c/$1). */
  matchingRate: number;
  /** Lower income threshold — full max available at/below (e.g. $47,488). */
  lowerThreshold: number;
  /** Higher income threshold — nil at/above (cut-out) (e.g. $62,488). */
  higherThreshold: number;
  /** Taper per $1 of total income above the lower threshold (e.g. 0.03333). */
  taperRate: number;
  /** Maximum age at year end to qualify (must be UNDER this) (e.g. 71). */
  maxAgeExclusive: number;
  /**
   * Total-superannuation-balance ceiling tested at the prior 30 June — must be
   * BELOW this (the general transfer balance cap, e.g. $2,000,000 for 2025-26).
   */
  tsbCap: number;
}

/**
 * Spouse-contribution tax offset. 18% of the lesser of (a) the
 * `maxContributionBase` reduced $1-for-$1 once the spouse's income exceeds
 * `fullOffsetThreshold`, and (b) the actual contribution made for the spouse.
 * Nil once spouse income reaches `cutOutThreshold`.
 */
export interface SpouseOffsetRules {
  /** Offset rate on the eligible contribution base (e.g. 0.18 = 18%). */
  offsetRate: number;
  /** Eligible-contribution base before taper (e.g. $3,000). */
  maxContributionBase: number;
  /** Spouse income at/below which the full base applies (e.g. $37,000). */
  fullOffsetThreshold: number;
  /** Spouse income at/above which the offset is nil (e.g. $40,000). */
  cutOutThreshold: number;
  /**
   * Spouse-side TSB ceiling (general transfer balance cap immediately before the
   * year) — informational gate, mirrors the co-contribution TSB cap.
   */
  tsbCap: number;
}

/** Carry-forward concessional-cap rule constants. */
export interface CarryForwardRules {
  /** Length of the rolling look-back window in years (e.g. 5). */
  windowYears: number;
  /** First financial year a carry-forward amount could be accrued (2018-19). */
  firstAccrualFy: FinancialYear;
  /**
   * Total-super-balance gate to USE any carry-forward amount: TSB must be BELOW
   * this at 30 June of the previous FY (e.g. $500,000, unindexed).
   */
  tsbGate: number;
}

/** Low Income Super Tax Offset (LISTO) — refunds the 15% in-fund tax on
 * concessional contributions for low earners, capped. */
export interface ListoRules {
  /** Maximum offset (e.g. $500). */
  max: number;
  /** Adjusted-taxable-income ceiling to qualify (≤ this, e.g. $37,000). */
  incomeThreshold: number;
  /** Rate applied to concessional contributions (e.g. 0.15 = 15%). */
  rate: number;
}

// ---------------------------------------------------------------------------
// coContribution
// ---------------------------------------------------------------------------

/**
 * Eligibility flags for the co-contribution gate. The engine does NOT determine
 * eligibility from the clock or from external data — the caller asserts each
 * fact and the engine reports whether the gate passes. Defaults are the common
 * qualifying case so a minimal call still computes the entitlement amount.
 */
export const CoContributionEligibilitySchema = z.object({
  /** Made an eligible (non-concessional, after-tax) personal contribution. */
  madeNonConcessionalContribution: z.boolean().default(true),
  /** ≥10% of total income is from employment and/or business (work test). */
  tenPercentEligibleIncome: z.boolean().default(true),
  /** Age at the end of the financial year (must be UNDER maxAgeExclusive). */
  ageAtYearEnd: z.number().int().min(0).max(130),
  /** Total super balance at the prior 30 June (tested below tsbCap). */
  totalSuperBalance: z.number().min(0).default(0),
  /** Lodging a tax return for the year (the mechanism the ATO pays through). */
  lodgesReturn: z.boolean().default(true),
  /** Holds a disqualifying temporary visa (NZ/prescribed visas excepted). */
  disqualifyingTempVisa: z.boolean().default(false),
});
export type CoContributionEligibility = z.input<
  typeof CoContributionEligibilitySchema
>;

export const CoContributionInputSchema = z.object({
  /**
   * Total income for the co-contribution income test (assessable income +
   * reportable fringe benefits + reportable employer super, less allowable
   * business deductions). Passed in — never derived.
   */
  totalIncome: z.number().min(0),
  /** The eligible (non-concessional, after-tax) personal contribution made. */
  personalContribution: z.number().min(0),
  /** Eligibility facts for the gate (age + work test + TSB + visa + return). */
  eligibility: CoContributionEligibilitySchema,
  /** The financial year being assessed, e.g. "2025-26". Passed in. */
  assessmentYear: z.string(),
});
export type CoContributionInput = z.input<typeof CoContributionInputSchema>;

export interface CoContributionOutput {
  /** Total income the income-test math ran against. */
  totalIncome: number;
  /** Eligible personal contribution considered. */
  personalContribution: number;
  /** Whether every eligibility condition passed (the gate). */
  eligible: boolean;
  /** Which conditions failed (empty when eligible). Human-stable keys. */
  failedConditions: readonly string[];
  /**
   * The co-contribution the ATO would pay IF eligible, whole dollars. This is
   * the entitlement AMOUNT (income-test × contribution-cap), independent of the
   * boolean gate, so the UI can show "you would qualify for $X if the conditions
   * are met". Zero when not eligible.
   */
  coContribution: number;
  /**
   * The maximum the income test alone allows (before the contribution cap and
   * the eligibility gate) — used to explain the taper. Zero at/above the higher
   * threshold.
   */
  incomeTestMax: number;
}

// ---------------------------------------------------------------------------
// spouseOffset
// ---------------------------------------------------------------------------

export const SpouseOffsetInputSchema = z.object({
  /** Spouse's income for the offset test (assessable + RFB + reportable super). */
  spouseIncome: z.number().min(0),
  /** Contribution you made to your spouse's super for the year. */
  contribution: z.number().min(0),
  /** The financial year being assessed, e.g. "2025-26". Passed in. */
  assessmentYear: z.string(),
});
export type SpouseOffsetInput = z.input<typeof SpouseOffsetInputSchema>;

export interface SpouseOffsetOutput {
  /** Spouse income the taper ran against. */
  spouseIncome: number;
  /** Contribution considered. */
  contribution: number;
  /** The eligible contribution base after the $1-for-$1 taper (≤ maxBase). */
  eligibleBase: number;
  /** The spouse-contribution tax offset, whole dollars. */
  offset: number;
  /** True while the spouse income is below the cut-out (offset can be > 0). */
  withinCutOut: boolean;
}

// ---------------------------------------------------------------------------
// carryForwardHeadroom
// ---------------------------------------------------------------------------

/** One financial year's concessional position for the carry-forward window. */
export const ConcessionalYearSchema = z.object({
  /** Financial year key, e.g. "2021-22". */
  fy: z.string(),
  /** General concessional cap that applied that FY (e.g. $27,500). */
  cap: z.number().min(0),
  /** Concessional contributions actually made that FY (SG + sacrifice + etc.). */
  contributions: z.number().min(0),
});
export type ConcessionalYear = z.input<typeof ConcessionalYearSchema>;

export const CarryForwardInputSchema = z.object({
  /**
   * Per-FY concessional history for the years in (or about to enter) the rolling
   * window. The engine sorts oldest-first and keeps only the years inside the
   * window for the assessment FY. Years outside the window are ignored (expired).
   */
  history: z.array(ConcessionalYearSchema),
  /**
   * Total super balance at 30 June of the previous FY. Carry-forward amounts may
   * only be USED when this is below the $500k gate. Passed in.
   */
  priorYearTsb: z.number().min(0),
  /**
   * Marginal income-tax rate (fraction, INCLUDING Medicare levy where it applies)
   * on the contributor's next dollar — supplied by the caller, who derives it
   * from the income-tax engine. Used for the marginal-vs-15% tax-saving figure.
   */
  marginalRate: z.number().min(0).max(1),
  /** The financial year being assessed, e.g. "2025-26". Passed in. */
  assessmentYear: z.string(),
});
export type CarryForwardInput = z.input<typeof CarryForwardInputSchema>;

export interface CarryForwardOutput {
  /** Total unused concessional cap available to carry forward, this FY's window. */
  availableHeadroom: number;
  /** Per-FY unused amounts inside the window (oldest first). */
  perYear: readonly {
    fy: string;
    cap: number;
    contributions: number;
    /** Unused = max(0, cap − contributions). */
    unused: number;
    /** True if this slice expires at the end of the current assessment FY. */
    expiresThisFy: boolean;
  }[];
  /** Whether the TSB gate allows USING any carry-forward amount this FY. */
  canUse: boolean;
  /**
   * The oldest in-window slice's expiry FY + its unused dollars — a CALM dated
   * fact (no urgency framing). `null` when there is no in-window unused amount.
   */
  oldestExpiring: {
    fy: string;
    unused: number;
    /** The FY at the end of which this slice expires (30 June of that FY). */
    expiresEndOfFy: string;
  } | null;
  /**
   * Illustrative tax differential on the available headroom: marginal − 15%
   * in-fund, on the headroom amount. Mechanics, not a "save $X" claim; zero when
   * the TSB gate blocks use. Whole dollars.
   */
  taxSavingIfContributed: number;
}

// ---------------------------------------------------------------------------
// sacrificeTaxSaving
// ---------------------------------------------------------------------------

export const SacrificeInputSchema = z.object({
  /** Amount the user plans to salary-sacrifice / claim as personal-deductible. */
  contribution: z.number().min(0),
  /**
   * Marginal income-tax rate (fraction, INCLUDING Medicare levy where it
   * applies) on the contributor's next dollar — derived by the caller from the
   * income-tax engine. The concession is `marginalRate − 15%` per $1.
   */
  marginalRate: z.number().min(0).max(1),
  /**
   * Adjusted taxable income for the LISTO test, if the caller wants LISTO
   * reported. Omit to skip LISTO.
   */
  adjustedTaxableIncome: z.number().min(0).optional(),
  /** The financial year being assessed, e.g. "2025-26". Passed in. */
  assessmentYear: z.string(),
});
export type SacrificeInput = z.input<typeof SacrificeInputSchema>;

export interface SacrificeOutput {
  /** Contribution considered. */
  contribution: number;
  /** Marginal rate (incl. Medicare) the concession was computed against. */
  marginalRate: number;
  /** In-fund concessional tax rate (15%). */
  concessionalTaxRate: number;
  /**
   * Net tax concession per $1: marginalRate − concessionalTaxRate. The LOW-INCOME
   * HONESTY value: this is small-but-POSITIVE in the 16% bracket (16% − 15% = 1%,
   * or 18% − 15% = 3% once 2% Medicare bites) and does NOT invert (go negative)
   * anywhere at/above the $18,200 tax-free threshold. It can only be negative
   * below $18,200, where the marginal rate is 0% and the 15% in-fund tax is a
   * cost — LISTO then refunds that 15% up to the LISTO income threshold.
   */
  concessionPerDollar: number;
  /** Total concession on the contribution: concessionPerDollar × contribution. */
  taxSaving: number;
  /** True when the concession per dollar is at or below zero (≤ $18,200 only). */
  concessionInverts: boolean;
  /** LISTO offset, if adjustedTaxableIncome was supplied and ≤ threshold. */
  listo: number | null;
}
